import { useState } from 'react';
import { Check, Zap, X, FileText, RotateCw } from 'lucide-react';
import { money } from '../LiveBasket';

/* Modal showing deliverables before "select" */
function TierDetailModal({ tier, onClose, onConfirm, isSelected }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-[fadeIn_0.2s_ease]" />
      <div
        className="relative w-full sm:max-w-lg bg-white dark:bg-[#0D0D0D] border border-slate-200 dark:border-white/10 rounded-t-3xl sm:rounded-2xl p-6 sm:p-8 max-h-[85vh] overflow-y-auto animate-[slideUp_0.3s_ease]"
        onClick={(e) => e.stopPropagation()}
        data-testid={`tier-modal-${tier.id}`}
      >
        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 dark:text-slate-500 hover:text-[#0A0A0A] dark:hover:text-white hover:bg-slate-200 dark:hover:bg-white/10 transition-colors">
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#00E5FF]">{tier.tagline}</span>
        </div>
        <h3 className="font-['Outfit'] text-3xl font-bold text-[#0A0A0A] dark:text-white mb-2">{tier.name}</h3>
        <p className="font-['Outfit'] text-4xl font-black text-[#00E5FF] mb-4">{money(tier.price)}<span className="text-sm text-slate-500 dark:text-slate-500 font-normal ml-1">one-time</span></p>

        {(tier.pages || tier.revisions) && (
          <div className="flex flex-wrap items-center gap-2 mb-5">
            {tier.pages && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-300">
                <FileText className="w-3 h-3 text-[#00E5FF]" />
                {tier.pages}
              </span>
            )}
            {tier.revisions && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-300">
                <RotateCw className="w-3 h-3 text-[#00E5FF]" />
                {tier.revisions}
              </span>
            )}
          </div>
        )}

        <p className="text-sm text-slate-400 dark:text-slate-400 leading-relaxed mb-5">{tier.description}</p>

        <p className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-500 mb-2">What's included</p>
        <ul className="space-y-2 mb-6">
          {(tier.deliverables || []).map((d, i) => (
            <li key={i} className="flex items-start gap-2">
              <Check className="w-4 h-4 text-[#00E5FF] mt-0.5 flex-shrink-0" />
              <span className="text-sm text-slate-400 dark:text-slate-300">{d}</span>
            </li>
          ))}
        </ul>

        <button
          type="button"
          data-testid={`tier-confirm-${tier.id}`}
          onClick={onConfirm}
          className={`w-full py-3.5 rounded-xl text-sm font-bold transition-all active:scale-[0.98] ${isSelected ? 'bg-emerald-400 text-[#0A0A0A]' : 'bg-[#00E5FF] text-[#0A0A0A] hover:bg-white hover:shadow-[0_0_24px_rgba(0,229,255,0.5)]'}`}
        >
          {isSelected ? '✓ Selected' : `Choose ${tier.name}`}
        </button>
      </div>
    </div>
  );
}

function TierCard({ tier, selected, onOpen }) {
  return (
    <button
      type="button"
      data-testid={`tier-card-${tier.id}`}
      onClick={onOpen}
      className={`group relative text-left rounded-2xl border p-5 sm:p-6 transition-all duration-300 overflow-hidden active:scale-[0.99]
        ${selected
          ? 'border-[#00E5FF] bg-[#00E5FF]/10 shadow-[0_0_32px_rgba(0,229,255,0.15)]'
          : 'border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.02] hover:border-slate-300 dark:border-white/25 hover:bg-slate-100 dark:bg-white/[0.04]'}`}
    >
      {selected && (
        <div className="absolute top-3 right-3 flex items-center gap-1 bg-[#00E5FF] text-[#0A0A0A] px-2 py-0.5 rounded-full text-[10px] font-bold">
          <Check className="w-3 h-3" /> Selected
        </div>
      )}
      <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-[#00E5FF]/5 blur-[40px] group-hover:bg-[#00E5FF]/15 transition-colors pointer-events-none" />
      <div className="relative">
        <div className="w-9 h-9 rounded-lg bg-[#00E5FF]/10 border border-[#00E5FF]/20 flex items-center justify-center mb-4">
          <Zap className="w-4 h-4 text-[#00E5FF]" />
        </div>
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-500 mb-1">{tier.tagline}</p>
        <h3 className="font-['Outfit'] text-xl sm:text-2xl font-bold text-[#0A0A0A] dark:text-white mb-2">{tier.name}</h3>
        <p className="font-['Outfit'] text-3xl sm:text-4xl font-black text-[#0A0A0A] dark:text-white mb-3">{money(tier.price)}<span className="text-xs text-slate-500 dark:text-slate-500 font-normal ml-1">one-time</span></p>

        {/* Stat chips — pages + revisions */}
        {(tier.pages || tier.revisions) && (
          <div className="flex flex-wrap items-center gap-1.5 mb-3">
            {tier.pages && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-300">
                <FileText className="w-2.5 h-2.5 text-[#00E5FF]" />
                {tier.pages}
              </span>
            )}
            {tier.revisions && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-300">
                <RotateCw className="w-2.5 h-2.5 text-[#00E5FF]" />
                {tier.revisions}
              </span>
            )}
          </div>
        )}

        <p className="text-xs text-slate-400 dark:text-slate-400 leading-relaxed line-clamp-2 mb-3">{tier.description}</p>
        <span className="inline-flex items-center gap-1 text-[11px] text-[#00E5FF] group-hover:gap-2 transition-all">See what's included →</span>
      </div>
    </button>
  );
}

export default function StepFoundation({ tiers, selected, onSelect }) {
  const [modalTier, setModalTier] = useState(null);

  return (
    <div data-testid="step-foundation" className="animate-[fadeIn_0.4s_ease]">
      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-widest text-[#00E5FF] mb-2 font-bold">Step 1 · Foundation</p>
        <h2 className="font-['Outfit'] text-2xl sm:text-4xl font-bold text-[#0A0A0A] dark:text-white leading-tight">Pick your project base</h2>
        <p className="text-sm text-slate-400 dark:text-slate-400 mt-2 max-w-md">Tap a package to see what's included — confirm it and you'll jump straight to the power-ups.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        {tiers.map(t => (
          <TierCard
            key={t.id}
            tier={t}
            selected={selected?.id === t.id}
            onOpen={() => setModalTier(t)}
          />
        ))}
      </div>

      {modalTier && (
        <TierDetailModal
          tier={modalTier}
          isSelected={selected?.id === modalTier.id}
          onClose={() => setModalTier(null)}
          onConfirm={() => { onSelect(modalTier); setModalTier(null); }}
        />
      )}
    </div>
  );
}
